import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, Stack, Divider, IconButton, Alert } from '@mui/material';
import { Save, Close } from '@mui/icons-material';
import { useMsal, useAccount } from '@azure/msal-react';
import { useAppDispatch, useAppSelector } from '../../../../../app/hooks';
import { useUpsertTeamMutation, useGetTeamByIdQuery, TeamView, TeamUpsertRequest } from '../../../../../services/TeamService';
import { initializeTeam, updateTeam } from '../../../state';
import { TeamFormFields, TeamMembersList, AddTeamMember, TeamCategorySelection } from './index';
import { useTeamFormValidation } from '../../../hooks';

interface TeamAddEditDialogProps {
  open: boolean;
  onClose: () => void;
  teamId?: string | null; // null or undefined for add mode, string for edit mode
  onSave?: (team: TeamView) => void;
}

export const TeamAddEditDialog: React.FC<TeamAddEditDialogProps> = ({ 
  open, 
  onClose, 
  teamId, 
  onSave 
}) => {
  const dispatch = useAppDispatch();
  const [upsertTeam, { isLoading: isSaving }] = useUpsertTeamMutation();
  
  // Get current authenticated user
  const { accounts } = useMsal();
  const account = useAccount(accounts[0] || {});
  const currentUserName = account?.name || 'Unknown User';
  
  // Get team data from Redux store
  const currentTeam = useAppSelector(state => state.team.currentTeam);
  const isEdited = useAppSelector(state => state.team.isEdited);
  
  // Fetch team data when editing
  const { data: fetchedTeam, isLoading: isLoadingTeam } = useGetTeamByIdQuery(
    teamId || '', 
    { skip: !teamId || !open }
  );
  
  // Local state for errors
  const [errors, setErrors] = useState<string[]>([]);
  
  const isEditMode = Boolean(teamId);
  const formData = currentTeam;
  
  // Use validation hook
  const { canSubmit, touchAll } = useTeamFormValidation(formData);

  // Initialize team data when dialog opens
  useEffect(() => {
    if (open) {
      if (isEditMode && teamId) {
        // Edit mode - team data will be loaded by the API query and Redux
        // The extraReducers in teamSlice will handle setting currentTeam
      } else {
        // Add mode - initialize with default team
        dispatch(initializeTeam());
        // Set the current user as the creator
        dispatch(updateTeam({ 
          createdBy: currentUserName, 
          modifiedBy: currentUserName 
        }));
      }
      setErrors([]);
    }
  }, [open, isEditMode, teamId, dispatch]);

  const handleSave = async () => {
    touchAll();
    if (!canSubmit() || !formData) {
      return;
    }

    try {
      const request: TeamUpsertRequest = {
        teamView: {
          ...formData,
          createdBy: isEditMode ? formData.createdBy : currentUserName,
          modifiedBy: currentUserName
        },
        modifiedBy: currentUserName
      };

      const result = await upsertTeam(request).unwrap();
      
      if (onSave) {
        onSave(result);
      }
      
      handleClose();
    } catch (error) {
      console.error('Failed to save team:', error);
      setErrors(['Failed to save team. Please try again.']);
    }
  };

  const handleClose = () => {
    setErrors([]);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle sx={{ 
        backgroundColor: '#f8fafc',
        borderBottom: '1px solid #e2e8f0',
        fontWeight: 600,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        {isEditMode ? `Edit Team: ${formData?.name || ''}` : 'Add New Team'}
        <IconButton onClick={handleClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ pt: 3 }}>
        {isLoadingTeam && isEditMode ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <Typography variant="body1" color="textSecondary">
              Loading team data...
            </Typography>
          </Box>
        ) : (
          <>
            {(errors || []).length > 0 && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {(errors || []).map((error, index) => (
                  <div key={index}>{error}</div>
                ))}
              </Alert>
            )}

        <Stack spacing={3}>
          {/* Basic Team Information */}
          <Box>
            <Typography variant="subtitle1" fontWeight={600} color="#1e293b" sx={{ mb: 2 }}>
              Team Information
            </Typography>
            <TeamFormFields />
          </Box>

          <Divider />

          {/* Team Members */}
          <Box>
            <Typography variant="subtitle1" fontWeight={600} color="#1e293b" sx={{ mb: 2 }}>
              Team Members ({(formData?.members || []).length})
            </Typography>
            
            <TeamMembersList />
            
            <Box sx={{ mt: 2 }}>
              <AddTeamMember />
            </Box>
          </Box>

          <Divider />

          {/* Category Assignments */}
          <Box>
            <TeamCategorySelection />
          </Box>
        </Stack>
          </>
        )}
      </DialogContent>
      
      <DialogActions sx={{ p: 2.5, backgroundColor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
        <Button 
          onClick={handleClose} 
          disabled={isSaving}
          sx={{ color: '#64748b' }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={isSaving || !canSubmit()}
          startIcon={isSaving ? undefined : <Save />}
          sx={{
            backgroundColor: '#162346',
            color: 'white',
            fontWeight: 600,
            '&:hover': {
              backgroundColor: '#1a2a52'
            }
          }}
        >
          {isSaving ? 'Saving...' : (isEditMode ? 'Update Team' : 'Create Team')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TeamAddEditDialog;